import {
  ButtonGroup,
  Paper,
  styled,
  Box,
  Divider,
  TextField,
  Grid,
  InputAdornment,
  IconButton,
  Button,
  useTheme,
  Theme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { OpsLinkEventTable } from "./OpsLinkEventTable";
import { OpsLinkMessagePriority, IOpsLinkMessage, IFlight } from "@vmosolutions/oc-interfaces";
import { useMemo, useCallback, useRef, useState, useEffect } from "react";
import _ from "lodash";
import classNames from "classnames";
import SpaceDashboardIcon from "@mui/icons-material/SpaceDashboard";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import PreviewIcon from "@mui/icons-material/Preview";
import { framePositions } from "./OpsLinkWindow";

interface IOpsLinkEventList {
  id: string;
  headerHeight: number;
  selectedEvents: IOpsLinkMessage[];
  setCurrentEvent: React.Dispatch<React.SetStateAction<IOpsLinkMessage>>;
  setReplyEvent: React.Dispatch<React.SetStateAction<IOpsLinkMessage>>;
  disabledPriorities: string[];
  setDisabledPriorities: React.Dispatch<React.SetStateAction<string[]>>;
  searchVal: string;
  setSearchVal: React.Dispatch<React.SetStateAction<string>>;
  framePos: framePositions;
  setFramePos: React.Dispatch<React.SetStateAction<framePositions>>;
  trackedFlights: IFlight[];
}

const OCOpsLinkEventList = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "headerHeight" && prop !== "theme",
})((prop: { headerHeight: number; theme: Theme }) => ({
  padding: "10px",
  width: "100%",
  height: "100%",
  "& .MuiInputBase-input": {
    padding: "5px",
  },
  "& .urgencyBox": {
    display: "flex",
    alignItems: "center",
    textAlign: "center",
    border: "1px solid white",
    marginRight: "5px",
    color: "white",
    cursor: "pointer",
    "& .MuiBox-root": {
      width: "100px",
    },
  },
  "& .urgencyDisabled": {
    border: "1px solid #444 !important",
    color: "#ccc !important",
  },
  "& .urgencyPopulated": {
    fontWeight: "bold",
  },
  "& .highlightedFrame": {
    backgroundColor: `${prop.theme.palette.primary.main}`,
  },
}));

export default function OpsLinkEventList(props: IOpsLinkEventList): JSX.Element {
  const {
    id,
    headerHeight,
    selectedEvents,
    setCurrentEvent,
    setReplyEvent,
    disabledPriorities,
    setDisabledPriorities,
    setSearchVal,
    searchVal,
    framePos,
    setFramePos,
    trackedFlights,
  } = props;

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const theme = useTheme();

  useEffect(() => {
    const node = tableContainerRef.current;
    if (!node) return undefined;

    const reservedHeight = 160;
    function updateHeight() {
      const measured = tableContainerRef.current?.getBoundingClientRect().height;
      if (measured) setContainerHeight(Math.max(0, measured - reservedHeight));
    }

    updateHeight();

    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(updateHeight);
      observer.observe(node);
    }
    window.addEventListener("resize", updateHeight);

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", updateHeight);
    };
  }, []);

  const updatedDisabledPriorities = useCallback(
    (priority: string) => {
      const tempPriorities = _.cloneDeep(disabledPriorities);
      if (tempPriorities.includes(priority)) {
        tempPriorities.splice(tempPriorities.indexOf(priority), 1);
      } else {
        tempPriorities.push(priority);
      }
      setDisabledPriorities(tempPriorities);
    },
    [disabledPriorities]
  );

  const priorityCounts = useMemo(() => {
    const priorities: { [key: string]: any } = {};
    const tempEvents = _.cloneDeep(selectedEvents);
    priorities["urgent"] = tempEvents.filter((event) => event.priority === OpsLinkMessagePriority.URGENT).length;
    priorities["high"] = tempEvents.filter((event) => event.priority === OpsLinkMessagePriority.HIGH).length;
    priorities["medium"] = tempEvents.filter((event) => event.priority === OpsLinkMessagePriority.MEDIUM).length;
    priorities["low"] = tempEvents.filter((event) => event.priority === OpsLinkMessagePriority.LOW).length;
    return priorities;
  }, [selectedEvents]);

  return (
    <OCOpsLinkEventList elevation={1} headerHeight={headerHeight} theme={theme} ref={tableContainerRef}>
      <Box
        sx={{
          height: "80px",
          width: "100%",
        }}
      >
        <TextField
          placeholder="Search..."
          color="primary"
          variant="filled"
          sx={{ width: "100%" }}
          value={searchVal}
          onChange={(e) => {
            const val = e.target.value;
            setSearchVal(val);
          }}
          InputProps={{
            sx: { verticalAlign: "middle", marginBottom: "5px" },
            startAdornment: (
              <InputAdornment sx={{ marginTop: "-1px !important" }} position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Grid container sx={{ justifyContent: "center", alignItems: "center" }}>
          <Grid item xs={3}>
            <IconButton>
              <FilterAltIcon />
            </IconButton>
          </Grid>
          <Grid item xs={9}>
            <Grid container sx={{ justifyContent: "end" }}>
              <Grid
                item
                className={classNames(
                  "urgencyBox",
                  disabledPriorities.includes("urgent") && "urgencyDisabled",
                  priorityCounts?.urgent > 0 && "urgencyPopulated"
                )}
              >
                <Box
                  sx={{ backgroundColor: theme.palette.error.main }}
                  onClick={() => updatedDisabledPriorities("urgent")}
                >
                  {priorityCounts?.urgent ?? 0} Urgent
                </Box>
              </Grid>
              <Grid
                item
                className={classNames(
                  "urgencyBox",
                  disabledPriorities.includes("high") && "urgencyDisabled",
                  priorityCounts?.high > 0 && "urgencyPopulated"
                )}
              >
                <Box sx={{ backgroundColor: "#af760c" }} onClick={() => updatedDisabledPriorities("high")}>
                  {priorityCounts?.high ?? 0} High
                </Box>
              </Grid>
              <Grid
                item
                className={classNames(
                  "urgencyBox",
                  disabledPriorities.includes("medium") && "urgencyDisabled",
                  priorityCounts?.medium > 0 && "urgencyPopulated"
                )}
              >
                <Box sx={{ backgroundColor: "blue" }} onClick={() => updatedDisabledPriorities("medium")}>
                  {priorityCounts?.medium ?? 0} Medium
                </Box>
              </Grid>
              <Grid
                item
                className={classNames(
                  "urgencyBox",
                  disabledPriorities.includes("low") && "urgencyDisabled",
                  priorityCounts?.low > 0 && "urgencyPopulated"
                )}
              >
                <Box sx={{ backgroundColor: "green" }} onClick={() => updatedDisabledPriorities("low")}>
                  {priorityCounts?.low ?? 0} Low
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
      <Divider sx={{ borderColor: "#666666" }} />
      <OpsLinkEventTable
        id={id}
        selectedEvents={selectedEvents}
        searchVal={searchVal}
        setCurrentEvent={setCurrentEvent}
        setReplyEvent={setReplyEvent}
        disabledPriorities={disabledPriorities}
        containerHeight={containerHeight}
        trackedFlights={trackedFlights}
      />
      <ButtonGroup variant="outlined" sx={{ marginTop: "10px" }}>
        <Button
          onClick={() => setFramePos(framePositions.BELOW)}
          className={framePos === framePositions.BELOW ? "highlightedFrame" : ""}
        >
          <SpaceDashboardIcon sx={{ color: "white" }} />
        </Button>
        <Button
          onClick={() => setFramePos(framePositions.SIDE)}
          disabled
          className={framePos === framePositions.SIDE ? "highlightedFrame" : ""}
        >
          <ViewColumnIcon sx={{ color: "white" }} />
        </Button>
        <Button
          onClick={() => setFramePos(framePositions.POPUP)}
          disabled
          className={framePos === framePositions.POPUP ? "highlightedFrame" : ""}
        >
          <PreviewIcon sx={{ color: "white" }} />
        </Button>
      </ButtonGroup>
    </OCOpsLinkEventList>
  );
}
